import { apiRequest } from "./apiClient";
import { rt } from "../i18n/runtime";
import { localScheduleFields } from "./scheduledNotificationService";
import { currentTimeZonePayload } from "../hooks/useUserTimezone";

const normalizeScheduledDateForStorage = (value) => {
  if (!value) return "";
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const normalizeUid = (u) => u?.uid || u?.id || "";
const temizUyeler = (uyeler = []) => uyeler.map((u) => ({ ...u, uid: normalizeUid(u) })).filter((u) => !!u.uid);

export async function aileBildirimiGonder({
  familyId,
  uyeler = [],
  hedefUidler = [],
  baslik,
  aciklama,
  gonderenUid,
  gonderimTuru = "normal",
  bildirimType = "familyAnnouncement",
  kaynak = "familyNotification",
  url = "/",
  idempotencyKey = null,
  respectQuietHours = false,
  cooldownSeconds = null,
  statusKey = null,
  statusUpdatedAt = null,
  statusRevision = null,
}) {
  if (!familyId || !gonderenUid) throw new Error(rt("service.error.familySenderMissing"));

  const hedefSet = new Set((hedefUidler || []).filter(Boolean));
  const hedefler = temizUyeler(uyeler).filter((u) => hedefSet.has(u.uid));
  const finalHedefUidler = hedefler.length ? hedefler.map((u) => u.uid) : [...hedefSet];

  if (finalHedefUidler.length === 0) throw new Error(rt("service.error.noFamilyMembersToSend"));

  const data = await apiRequest("/notifications/family/send", {
    method: "POST",
    body: JSON.stringify({
      familyId,
      hedefUidler: finalHedefUidler,
      hedefSayisi: finalHedefUidler.length,
      baslik,
      aciklama,
      gonderimTuru,
      gonderenUid,
      bildirimType,
      type: bildirimType,
      kaynak,
      url,
      idempotencyKey,
      respectQuietHours,
      cooldownSeconds,
      ...(statusKey ? { statusKey } : {}),
      ...(statusUpdatedAt ? { statusUpdatedAt } : {}),
      ...(statusRevision ? { statusRevision } : {}),
    }),
  });

  return { id: data?.id, adet: data?.adet ?? data?.count ?? finalHedefUidler.length };
}

export async function aileZamanliBildirimiOlustur({
  familyId,
  hedefUidler = [],
  baslik,
  aciklama,
  planlananTarih,
  tekrarTipi = "none",
  gonderimTuru = "normal",
  olusturanUid,
}) {
  if (!familyId || !olusturanUid) throw new Error(rt("service.error.familyCreatorMissing"));

  const data = await apiRequest("/notifications/scheduled", {
    method: "POST",
    body: JSON.stringify({
      hedefTipi: "familyUsers",
      familyId,
      hedefUidler: (hedefUidler || []).filter(Boolean),
      hedefSayisi: (hedefUidler || []).filter(Boolean).length,
      baslik,
      aciklama,
      planlananTarih: normalizeScheduledDateForStorage(planlananTarih),
      scheduleMode: "recipient_local",
      ...localScheduleFields(planlananTarih),
      scheduleTimezone: currentTimeZonePayload().timeZone,
      tekrarTipi,
      gonderimTuru,
      durum: "bekliyor",
      tip: "familyScheduled",
      bildirimType: "familyAnnouncement",
      kaynak: "familyNotification",
      olusturanUid,
      url: "/",
      idempotencyKey: `familyScheduled_${familyId}_${olusturanUid}_${new Date(planlananTarih).getTime()}`,
    }),
  });

  return data?.id || data?.item?.id;
}

export async function sonAileBildirimleriniGetir(familyId, adet = 5) {
  if (!familyId) return [];
  const data = await apiRequest(`/notifications/family/${encodeURIComponent(familyId)}/recent?limit=${adet}`);
  return data?.items || [];
}

export async function aileZamanliBildirimleriniGetir(familyId) {
  if (!familyId) return [];
  const params = new URLSearchParams({ familyId });
  const data = await apiRequest(`/notifications/scheduled?${params.toString()}`);
  return (data?.items || [])
    .filter((item) => (item.tip || item.raw?.tip) === "familyScheduled" || item.hedefTipi === "familyUsers")
    .filter((item) => item.durum !== "iptal" && item.durum !== "tamamlandi")
    .sort((a, b) => String(a.planlananTarih || "").localeCompare(String(b.planlananTarih || "")));
}

export async function aileZamanliBildirimiGuncelle(id, data = {}) {
  if (!id) return;
  const payload = { ...data, durum: "bekliyor" };
  if (Object.prototype.hasOwnProperty.call(data, "planlananTarih")) {
    payload.planlananTarih = normalizeScheduledDateForStorage(data.planlananTarih);
    Object.assign(payload, localScheduleFields(data.planlananTarih), {
      scheduleMode: "recipient_local",
      scheduleTimezone: currentTimeZonePayload().timeZone,
    });
  }
  await apiRequest(`/notifications/scheduled/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function aileZamanliBildirimiIptalEt(id) {
  if (!id) return;
  await apiRequest(`/notifications/scheduled/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function aileBildirimiOkunmaDurumuGetir({ familyId, bildirimId, hedefUidler = [] }) {
  const temizHedefUidler = [...new Set((hedefUidler || []).filter(Boolean))];
  if (!familyId || !bildirimId || temizHedefUidler.length === 0) {
    return { okunanSayisi: 0, hedefSayisi: temizHedefUidler.length, okuyanUidler: [], okuyanlar: [], okumayanlar: [] };
  }

  const params = new URLSearchParams({ targets: temizHedefUidler.join(",") });
  const data = await apiRequest(
    `/notifications/family/${encodeURIComponent(familyId)}/read-status/${encodeURIComponent(bildirimId)}?${params.toString()}`
  );

  return {
    okunanSayisi: data?.okunanSayisi || 0,
    hedefSayisi: data?.hedefSayisi ?? temizHedefUidler.length,
    okuyanUidler: data?.okuyanUidler || [],
    okuyanlar: data?.okuyanlar || [],
    okumayanlar: data?.okumayanlar || [],
  };
}
